import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import tenderService from '../../../api/crm/tenders/tenderService';
import { formatDate } from '../../../utils/stringUtils';
import { toast } from 'react-hot-toast';
import { formatIndianAmount } from '../../../utils/amountFormatter';

const TenderDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const fetchTender = async () => {
      setLoading(true);
      try {
        const result = await tenderService.getTenderById(id);
        if (result.success) {
          setTender(result.data);
        } else {
          toast.error(result.error || 'Failed to load tender details');
        }
      } catch (error) {
        toast.error('An error occurred while loading tender details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTender();
    }
  }, [id]);

  const handleExport = async () => {
    if (isExporting || !id) return;
    setIsExporting(true);
    const toastId = toast.loading('Preparing Excel export...');
    try {
      const exportName = tender?.tenderId || tender?.title || tender?.organization || 'Tender';
      const result = await tenderService.exportTenderDetail(id, exportName);
      if (result.success) {
        toast.success('Tender exported successfully', { id: toastId });
      } else {
        toast.error(result.error || 'Failed to export tender', { id: toastId });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to export tender', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-agrosaf-green" />
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/tender_lead')}
          className="flex-shrink-0"
        >
          Back to Tenders
        </Button>
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <p className="text-gray-600">Tender not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/tender_lead')}
            className="flex-shrink-0"
          >
            Back to Tenders
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 capitalize">
              {tender.title}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 capitalize">
              {tender.organization}
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-2">
          <Button
            variant="outline"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExport}
            loading={isExporting}
            disabled={isExporting}
            className="shadow-sm whitespace-nowrap"
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-gray-100 text-gray-800">
            {tender.status || 'Open'}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-gray-100 text-gray-800">
            {tender.priority || 'Medium'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Main details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Tender Title</p>
                <p className="font-medium text-gray-900 break-words capitalize">
                  {tender.title}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Organization</p>
                <p className="font-medium text-gray-900 break-words capitalize">
                  {tender.organization}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Tender Value</p>
                <p className="font-medium text-gray-900 break-words">
                  ₹{tender.tenderValue
                    ? formatIndianAmount(tender.tenderValue)
                    : '0'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Location</p>
                <p className="text-gray-900 break-words capitalize">
                  {tender.location || '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Assigned To</p>
                <p className="text-gray-900 break-words">
                  {tender.assignedTo && typeof tender.assignedTo === 'object'
                    ? `${tender.assignedTo.firstName || ''} ${tender.assignedTo.lastName || ''} (${tender.assignedTo.email || ''})`
                    : (tender.assignedTo || 'Unassigned')}
                </p>
              </div>
            </div>
          </div>

          {/* Important Dates */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Important Dates</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Submission Date</p>
                <p className="text-gray-900 break-words">
                  {formatDate(tender.submissionDate, { includeTime: false })}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Opening Date</p>
                <p className="text-gray-900 break-words">
                  {formatDate(tender.openingDate, { includeTime: false })}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Tender Description</h4>
            <p className="text-sm text-gray-600 break-words whitespace-pre-line first-letter:capitalize">
              {tender.description || '-'}
            </p>
          </div>
        </div>

        {/* Right: Contact info & meta */}
        <div className="space-y-4">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Contact Information</h4>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-gray-500">Contact Person</p>
                <p className="text-gray-900 capitalize">
                  {tender.contactPerson || '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Contact Email</p>
                <p className="text-gray-900 break-words">
                  {tender.contactEmail || '-'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Contact Phone</p>
                <p className="text-gray-900 break-words">
                  {tender.contactPhone || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Meta */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Meta</h4>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-gray-500">Tender ID</p>
                <p className="text-gray-900">{tender.tenderId || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500">Created At</p>
                <p className="text-gray-900">
                  {formatDate(tender.createdAt, { includeTime: true })}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Last Updated</p>
                <p className="text-gray-900">
                  {formatDate(tender.updatedAt, { includeTime: true })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenderDetail;


